import {
    Button,
    Group,
    Modal,
    NumberInput,
    Select,
    SimpleGrid,
    Stack,
    Text,
    Textarea,
    TextInput,
} from '@mantine/core'
import {useEffect, useState} from 'react'

import {
    chargeTotal,
    chargeUnitOptions,
    normalizeChargeUnit,
} from '../jobs/financial-line-item-form-utils'
import {formatPrice} from '../lib/format-price'
import {parseNumberInputValueOr} from '../lib/parse-number-input-value'
import type {ProjectBillableLineItem, ProjectBillingTerms} from './schemas'
import {useCreateProjectBillableLineItem, useUpdateProjectBillableLineItem} from './use-projects'

type ProjectFinancialLineItemModalProps = {
    opened: boolean
    lineItem?: ProjectBillableLineItem
    // null sends an explicit empty PO instead of inheriting the project PO.
    initialDraft?: Partial<Omit<ReturnType<typeof emptyDraft>, 'poNumber'>> & {
        poNumber?: string | null
    }
    projectId: string
    jobs: Array<{id: string; name: string}>
    billingTerms: ProjectBillingTerms[]
    orderPoNumber?: string | null
    onClose: () => void
}

function emptyDraft(): {
    jobId: string | null
    chargeType: string
    name: string
    description: string
    quantity: number
    unit: string
    unitPrice: number
    poNumber: string
    projectBillingTermsId: string | null
} {
    return {
        jobId: null,
        chargeType: 'manual',
        name: '',
        description: '',
        quantity: 1,
        unit: 'each',
        unitPrice: 0,
        poNumber: '',
        projectBillingTermsId: null,
    }
}

export function ProjectFinancialLineItemModal({
    opened,
    lineItem,
    initialDraft,
    projectId,
    jobs,
    billingTerms,
    orderPoNumber,
    onClose,
}: ProjectFinancialLineItemModalProps) {
    const isEditing = lineItem != null
    const [draft, setDraft] = useState(emptyDraft())
    const [errorMessage, setErrorMessage] = useState<string | null>(null)

    const createMutation = useCreateProjectBillableLineItem(projectId)
    const updateMutation = useUpdateProjectBillableLineItem(projectId)

    const saving = createMutation.isPending || updateMutation.isPending

    useEffect(() => {
        if (!opened) {
            return
        }

        if (lineItem) {
            setDraft({
                jobId: lineItem.jobId,
                chargeType: lineItem.chargeType,
                name: lineItem.name,
                description: lineItem.description,
                quantity: lineItem.quantity,
                unit: normalizeChargeUnit(lineItem.unit),
                unitPrice: lineItem.unitPrice,
                poNumber: lineItem.poNumber ?? '',
                projectBillingTermsId: lineItem.projectBillingTermsId,
            })
        } else if (initialDraft) {
            setDraft({
                ...emptyDraft(),
                ...initialDraft,
                poNumber: initialDraft.poNumber ?? '',
                unit: normalizeChargeUnit(initialDraft.unit ?? 'each'),
            })
        } else {
            setDraft(emptyDraft())
        }
        setErrorMessage(null)
    }, [opened, lineItem, initialDraft])

    function orderChargePo(): {poNumber?: string | null} {
        const poNumber = draft.poNumber.trim()
        if (poNumber) {
            return {poNumber}
        }

        return !isEditing && initialDraft?.poNumber === null ? {poNumber: null} : {}
    }

    async function handleSave() {
        const trimmedName = draft.name.trim()
        if (!trimmedName) {
            setErrorMessage('Description is required')
            return
        }

        if (draft.unitPrice < 0) {
            setErrorMessage('Unit price cannot be negative')
            return
        }

        if (draft.quantity <= 0) {
            setErrorMessage('Quantity must be greater than zero')
            return
        }

        const input = {
            jobId: draft.jobId,
            chargeType: draft.chargeType,
            name: trimmedName,
            description: draft.description,
            quantity: draft.quantity,
            unit: draft.unit,
            unitPrice: draft.unitPrice,
            totalPrice: chargeTotal(draft.quantity, draft.unitPrice),
            ...(draft.jobId === null ? orderChargePo() : {}),
            ...(draft.jobId === null && draft.projectBillingTermsId !== null
                ? {projectBillingTermsId: draft.projectBillingTermsId}
                : {}),
        }

        try {
            if (isEditing) {
                await updateMutation.mutateAsync({
                    lineItemId: lineItem.id,
                    input,
                })
            } else {
                await createMutation.mutateAsync(input)
            }
            onClose()
        } catch (error) {
            setErrorMessage(
                error instanceof Error ? error.message : 'Failed to save financial line item',
            )
        }
    }

    const jobOptions = [
        {value: 'project-level', label: 'Order-level charge'},
        ...jobs.map((job) => ({value: job.id, label: `Job: ${job.name}`})),
    ]
    const orderPo = orderPoNumber?.trim() || null

    return (
        <Modal opened={opened} onClose={onClose} title={isEditing ? 'Edit charge' : 'Add charge'}>
            <Stack gap="md">
                <Select
                    label="Scope"
                    data={jobOptions}
                    value={draft.jobId ?? 'project-level'}
                    onChange={(value) => {
                        setDraft((current) => ({
                            ...current,
                            jobId: value === 'project-level' ? null : value,
                        }))
                    }}
                    disabled={isEditing}
                />
                <TextInput
                    label="Description"
                    description="What the customer is charged for"
                    placeholder="e.g. Fuel tank, Diesel, Washout"
                    value={draft.name}
                    onChange={(event) => {
                        const name = event.currentTarget.value
                        setDraft((current) => ({...current, name}))
                    }}
                    required
                />
                <SimpleGrid cols={3}>
                    <NumberInput
                        label="Quantity"
                        min={0}
                        decimalScale={2}
                        value={draft.quantity}
                        onChange={(value) =>
                            setDraft((current) => ({
                                ...current,
                                quantity: parseNumberInputValueOr(value, 0),
                            }))
                        }
                    />
                    <Select
                        label="Unit"
                        data={chargeUnitOptions(draft.unit)}
                        value={draft.unit}
                        allowDeselect={false}
                        onChange={(value) => {
                            if (value) {
                                setDraft((current) => ({...current, unit: value}))
                            }
                        }}
                    />
                    <NumberInput
                        label={draft.unit === 'each' ? 'Rate (each)' : `Rate per ${draft.unit}`}
                        min={0}
                        prefix="$"
                        decimalScale={4}
                        value={draft.unitPrice}
                        onChange={(value) =>
                            setDraft((current) => ({
                                ...current,
                                unitPrice: parseNumberInputValueOr(value, 0),
                            }))
                        }
                    />
                </SimpleGrid>
                <Text size="sm">
                    Total: <b>{formatPrice(chargeTotal(draft.quantity, draft.unitPrice))}</b>
                </Text>
                <Text size="xs" c="dimmed">
                    One charge per rate, e.g. monthly or daily
                </Text>
                <Textarea
                    label="Note (optional)"
                    value={draft.description}
                    onChange={(event) => {
                        const description = event.currentTarget.value
                        setDraft((current) => ({...current, description}))
                    }}
                    minRows={2}
                />
                {draft.jobId === null ? (
                    <>
                        <TextInput
                            label="PO number (optional)"
                            value={draft.poNumber}
                            placeholder={orderPo ?? undefined}
                            description={
                                orderPo
                                    ? `Leave blank to use the order PO (${orderPo})`
                                    : 'Leave blank to use the order PO (none yet)'
                            }
                            onChange={(event) => {
                                const poNumber = event.currentTarget.value
                                setDraft((current) => ({...current, poNumber}))
                            }}
                        />
                        <Select
                            label="Billing terms"
                            placeholder="Use latest terms"
                            clearable
                            value={draft.projectBillingTermsId}
                            data={billingTerms.map((terms) => ({
                                value: terms.id,
                                label: `${terms.billingMethod} · ${new Date(terms.createdAt).toLocaleDateString()}`,
                            }))}
                            onChange={(projectBillingTermsId) =>
                                setDraft((current) => ({...current, projectBillingTermsId}))
                            }
                        />
                    </>
                ) : (
                    <Text size="xs" c="dimmed">
                        Uses the job PO and billing terms
                    </Text>
                )}
                {errorMessage && (
                    <Text size="sm" c="red" role="alert">
                        {errorMessage}
                    </Text>
                )}
                <Group justify="flex-end">
                    <Button variant="default" onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button onClick={() => void handleSave()} loading={saving}>
                        {isEditing ? 'Save changes' : 'Add charge'}
                    </Button>
                </Group>
            </Stack>
        </Modal>
    )
}
